import { ShieldCheck } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { cn } from '@/lib/utils';

interface AmfTrustBadgeProps {
  /** Visual tone — adapt to surface background. */
  tone?: 'sage' | 'inverse';
  className?: string;
}

/**
 * AmfTrustBadge — credential chip wellness-conversion (Phase 27.30).
 *
 * Visible signal réglementaire AMF (Autorité des marchés financiers Québec)
 * placé sur les touchpoints à enjeu : MidPageCta, OfferBouclier (drenched
 * terracotta → tone="inverse"), ContactForm, Hero TrustStrip.
 *
 * Wording validé par skill `intralys-amf-disclaimer` : "Inscrite à
 * l'Autorité des marchés financiers" (formulation réglementaire complète
 * dans l'aria-label, version compacte "Inscrite AMF · n° 271218" visible).
 *
 * Guard : ce composant assume que le numéro de certificat est connu et
 * verrouillé en translation (271218 pour Océanne, vérifié 2026-05-19).
 */
export function AmfTrustBadge({ tone = 'sage', className }: AmfTrustBadgeProps) {
  const { lang } = useLang();
  const trust = ta(t.trust, lang);
  // Phase 27.33 — Codex audit #1 fix : titre réglementaire AMF complet
  // (avant "Inscrite AMF · n° 271218" jugé tronqué). Le chip court reste
  // utilisé dans TrustStrip Hero où l'espace est contraint.
  const badge = trust.amf_badge_full;
  const aria = trust.amf_badge_aria;

  const toneClasses =
    tone === 'inverse'
      ? 'bg-background/10 text-background border-background/30'
      : 'surface-sage-soft text-foreground border-secondary/40';

  return (
    <span
      role="note"
      aria-label={aria}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-sans text-xs font-medium whitespace-nowrap',
        toneClasses,
        className
      )}
    >
      <ShieldCheck
        className={cn('h-3.5 w-3.5 shrink-0', tone === 'inverse' ? 'text-background' : 'text-primary')}
        strokeWidth={1.8}
        aria-hidden="true"
      />
      <span>{badge}</span>
    </span>
  );
}
